import PageSEO from '@/components/PageSEO';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, Phone, MessageSquare } from 'lucide-react';

export const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      category: 'General',
      description: 'Find answers to general questions about our products and services.',
      faqs: [
        {
          question: 'Why Choose Comal Glass Company for Shower Glass Enclosures?',
          answer: 'Comal Glass Company stands out as a premier provider of high-quality shower glass enclosures in the Texas Hill Country. With a commitment to craftsmanship and customer satisfaction, we offer a diverse range of customizable options to elevate your bathroom aesthetics. Our experienced installation team ensures precision fitting and lasting quality.',
        },
        {
          question: 'What Areas Does Comal Glass Company Serve?',
          answer: 'We proudly serve the Texas Hill Country and surrounding areas including Canyon Lake, New Braunfels, San Antonio, Boerne, Bulverde, Spring Branch, Wimberley, San Marcos, Austin, and surrounding communities. Contact us to confirm service availability in your area.',
        },
        {
          question: 'How Do I Get a Quote for My Glass Project?',
          answer: 'Getting a quote is easy! Simply contact our team by phone at (830) 359-9925, email us at orders@comalglass.com, or fill out our online contact form. We\'ll gather information about your preferences and specifications to provide you with a comprehensive and competitive quote. We also offer free on-site consultations for larger projects.',
        },
      ],
    },
    {
      category: 'Frameless Shower Enclosures',
      description: 'Learn about our custom frameless shower glass solutions.',
      faqs: [
        {
          question: 'What Types of Shower Glass Enclosures Does Comal Glass Company Offer?',
          answer: 'We provide a variety of shower glass enclosure configurations, including frameless and sliding frameless options. We also customize enclosures with numerous options including 3/8″ and 1/2″ glass in clear tempered, satin, rain, and obscure finish options. Each type offers unique features to suit different preferences and styles.',
        },
        {
          question: 'What Are the Advantages of Frameless Shower Glass Enclosures?',
          answer: 'Frameless shower glass enclosures offer several benefits:\n\n• Elegant Aesthetics: Frameless designs provide a sleek and modern look, enhancing the overall visual appeal of your bathroom.\n• Spacious Feel: The absence of bulky frames creates an open and spacious feel, making your bathroom appear larger.\n• Easy Maintenance: Frameless enclosures are easier to clean since they have fewer nooks and crannies where dirt and grime can accumulate.\n• Customization: Enjoy greater design flexibility with custom-crafted frameless enclosures tailored to your specific preferences and bathroom layout.',
        },
        {
          question: 'How Do Frameless Shower Glass Enclosures Compare to Framed Shower Glass in Terms of Durability?',
          answer: 'Frameless shower glass enclosures are known for their durability and longevity. The absence of frames reduces the risk of corrosion and deterioration, contributing to a longer lifespan compared to framed alternatives. Our tempered glass is 4-5 times stronger than regular glass and designed to withstand daily use.',
        },
        {
          question: 'Are Frameless Shower Glass Enclosures More Expensive?',
          answer: 'While frameless shower glass enclosures may have a slightly higher upfront cost, many customers find the investment worthwhile due to their aesthetic appeal, durability, and long-term value. Additionally, our customization options allow you to optimize costs based on your preferences. Frameless enclosures can also increase your home\'s resale value.',
        },
        {
          question: 'Can Frameless Shower Glass Enclosures Be Installed in Small Bathrooms?',
          answer: 'Yes, frameless shower glass enclosures are a great choice for small bathrooms. Their open and airy design can create the illusion of more space, making them a popular option for both large and compact bathrooms. Our team can design custom solutions for any bathroom size.',
        },
        {
          question: 'What Customization Options Are Available for Frameless Shower Glass Enclosures?',
          answer: 'Comal Glass Company offers a range of customization options for frameless shower glass enclosures:\n\n• Glass Thickness: Choose from 3/8″ or 1/2″ glass thickness options to meet your preferences and structural requirements.\n• Glass Types: Clear, low-iron ultra-clear, frosted, rain, and patterned options available.\n• Hardware Finishes: Select from chrome, brushed nickel, matte black, oil-rubbed bronze, and more to complement your bathroom decor.\n• Configuration: Customize the layout and design of your enclosure to fit your specific shower space.',
        },
      ],
    },
    {
      category: 'ShowerGuard Glass',
      description: 'Discover the benefits of our ShowerGuard protective coating.',
      faqs: [
        {
          question: 'What is ShowerGuard Glass?',
          answer: 'ShowerGuard is a revolutionary shower glass with a permanent protective coating that is applied during the manufacturing process. Unlike surface-applied treatments that wear off, ShowerGuard\'s protection is fused to the glass surface, creating a permanent barrier against corrosion, staining, and discoloration caused by hard water, soap, and humidity.',
        },
        {
          question: 'How is ShowerGuard Different from Regular Shower Glass?',
          answer: 'Regular shower glass is porous and allows residue to penetrate the surface, causing permanent staining and cloudiness over time. ShowerGuard\'s ion-beam coating seals the glass surface, preventing residue from bonding to the glass. This means your shower stays cleaner longer with less maintenance.',
        },
        {
          question: 'Does ShowerGuard Come with a Warranty?',
          answer: 'Yes! ShowerGuard glass comes with a Limited Lifetime Warranty from Guardian Glass, the manufacturer. This warranty covers the glass against staining, corrosion, and discoloration when properly maintained. Visit our Resources page to download the full warranty information and registration forms.',
        },
        {
          question: 'Is ShowerGuard Worth the Extra Cost?',
          answer: 'Many homeowners find ShowerGuard to be an excellent investment. The reduced cleaning time and effort, combined with the lifetime warranty and long-lasting clarity, provide excellent value over the life of your shower enclosure. Ask us about the current ShowerGuard rebate offer!',
        },
      ],
    },
    {
      category: 'Maintenance & Care',
      description: 'Tips for keeping your glass products looking their best.',
      faqs: [
        {
          question: 'What Maintenance is Required for Frameless Shower Glass Enclosures?',
          answer: 'Frameless shower glass enclosures are relatively low-maintenance. Regular cleaning with mild, non-abrasive cleaners is usually sufficient to keep them looking pristine. The absence of frames reduces the areas where dirt and soap scum can accumulate. We strongly recommend using a water softener to prevent calcium and lime deposits to help reduce overall maintenance and make cleaning easier.',
        },
        {
          question: 'How Do I Clean My Shower Glass?',
          answer: 'For regular cleaning, use a non-abrasive glass cleaner or a solution of mild dish soap and water. For hard water buildup, a mixture of equal parts white vinegar and water works well. Avoid abrasive cleaners, steel wool, or razor blades as these can scratch the glass. After each shower, use a squeegee to prevent water spots.',
        },
        {
          question: 'How Do I Care for My Mirrors?',
          answer: 'Clean mirrors with a standard glass cleaner and a soft, lint-free cloth. Avoid spraying cleaner directly on the mirror edges, as moisture can seep behind the mirror and damage the backing. For bathroom mirrors, ensure proper ventilation to reduce moisture exposure.',
        },
      ],
    },
    {
      category: 'Other Glass Products',
      description: 'Information about our full range of glass solutions.',
      faqs: [
        {
          question: 'Does Comal Glass Company Install Wine Cellar Enclosures?',
          answer: 'Yes! We specialize in custom glass wine cellar enclosures that showcase your collection while maintaining proper temperature control. Our thermally-broken glass systems help maintain optimal wine storage conditions while providing a stunning visual display. We offer frameless designs with various door configurations.',
        },
        {
          question: 'Do You Install Glass Railings?',
          answer: 'Absolutely! Glass railings are perfect for decks, balconies, staircases, and pool areas. Our glass railings provide safety without obstructing views, and we offer various mounting systems including frameless, post-mounted, and base shoe options. All installations meet local building codes.',
        },
        {
          question: 'What Mirror Services Do You Offer?',
          answer: 'We offer custom mirrors for any application including bathroom vanity mirrors, full-length mirrors, gym mirrors, antique mirrors, and decorative mirror walls. We can cut mirrors to any size and shape, and offer various edge finishes and frame options.',
        },
        {
          question: 'Do You Do Commercial Glass Work?',
          answer: 'Yes, we provide commercial glass services including storefront glass, office partitions, curtain walls, and commercial mirror installations. Our team has experience working with contractors, architects, and business owners on projects of all sizes.',
        },
      ],
    },
    {
      category: 'Warranty & Policies',
      description: 'Learn about our warranty coverage and business policies.',
      faqs: [
        {
          question: 'Do Your Products Come with a Warranty?',
          answer: 'Yes, Comal Glass Company stands behind the quality of our products and workmanship. Our frameless shower glass enclosures come with a warranty covering both materials and installation. Hardware carries a one-year warranty. ShowerGuard glass includes an additional Limited Lifetime Warranty from Guardian Glass. Visit our Resources page for complete warranty documents.',
        },
        {
          question: 'How Long Does Installation Take?',
          answer: 'Most residential shower enclosure installations are completed in a single day. From initial consultation to installation can happen in as little as one week, depending on customization requirements. We\'ll provide a specific timeline when you receive your quote.',
        },
        {
          question: 'Do You Offer Financing?',
          answer: 'Please contact us to discuss payment options for your project. We accept various payment methods and can discuss arrangements for larger projects.',
        },
      ],
    },
  ];

  let globalIndex = 0;

  // Generate FAQ Schema for all questions
  const allFaqs = faqCategories.flatMap(cat => cat.faqs);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.replace(/\n/g, ' ')
      }
    }))
  };

  return (
    <div className="min-h-screen">
      <PageSEO title="Frequently Asked Questions" description="Common questions about Comal Glass Company services, pricing, installation process, and service areas in the Texas Hill Country." path="/faq" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6">FAQ</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Frequently Asked <span className="text-gradient-cyan">Questions</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Quick answers to common questions about our shower glass, mirrors, and glass installation services in the Texas Hill Country.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-16">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <div className="mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">{category.category}</h2>
                  <p className="text-muted-foreground">{category.description}</p>
                </div>
                
                <div className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => {
                    const currentIndex = globalIndex++;
                    return (
                      <div
                        key={faqIndex}
                        className="bg-card rounded-lg shadow-sm border border-border overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(currentIndex)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-muted/50 transition-colors"
                        >
                          <span className="font-semibold text-foreground">{faq.question}</span>
                          <ChevronDown
                            className={`h-5 w-5 text-primary flex-shrink-0 transition-transform duration-200 ${
                              openIndex === currentIndex ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openIndex === currentIndex && (
                          <div className="px-6 pb-4">
                            <p className="text-muted-foreground whitespace-pre-line">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Have Additional <span className="text-gradient-cyan">Questions?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're here to help. Our team is ready to answer any questions about your glass project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">
                  <MessageSquare className="mr-2 h-5 w-5" />
                  Contact Us
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
